<script setup lang="ts">
import { computed } from "vue";
import { RouterLink } from "vue-router";
import type {
  DashboardNavExternalLink,
  DashboardNavLink,
} from "../../features/dashboard/dashboard.types.ts";
import AppPopover from "../AppPopover.vue";

const props = defineProps<{
  item: DashboardNavLink | DashboardNavExternalLink;
  isCollapsed: boolean;
  isActive: boolean;
  indented?: boolean;
}>();

const isExternal = computed(() => props.item.kind === "external");
const isDisabled = computed(() => props.item.disabled === true);

// One element renders both link kinds so the markup below stays single-source.
// A disabled entry stays in the list but is not a navigation control.
const linkComponent = computed(() => {
  if (isDisabled.value) {
    return "span";
  }
  return isExternal.value ? "a" : RouterLink;
});

const linkProps = computed(() => {
  if (isDisabled.value) {
    return { role: "link", "aria-disabled": "true" };
  }
  return props.item.kind === "external"
    ? { href: props.item.href, target: "_blank", rel: "noopener noreferrer" }
    : { to: { name: props.item.routeName } };
});
</script>

<template>
  <component
    :is="linkComponent"
    v-bind="linkProps"
    class="dashboard-sidebar-link flex items-start gap-3 rounded-xl border px-3 py-3 no-underline transition-colors"
    :class="[
      isCollapsed ? 'lg:items-center lg:justify-center lg:px-2 lg:py-3' : '',
      indented && !isCollapsed ? 'ps-6' : '',
      isDisabled
        ? 'dashboard-sidebar-link--disabled'
        : isActive
          ? 'dashboard-sidebar-link--active shadow-[0_10px_24px_rgb(8_33_71/28%)]'
          : 'dashboard-sidebar-link--inactive',
    ]"
    :title="isCollapsed ? item.label : undefined"
    :aria-label="item.label"
  >
    <span class="mt-0.5 text-lg leading-none">{{ item.icon }}</span>
    <span v-if="!isCollapsed" class="min-w-0">
      <AppPopover trigger="hover" placement="right" :width="220">
        <template #trigger>
          <span class="flex items-center gap-2 text-sm font-semibold">
            <span class="truncate">{{ item.label }}</span>
            <span v-if="isExternal" class="dashboard-sidebar-external-hint">
              ↗<span class="sr-only"> (opens in a new tab)</span>
            </span>
            <span
              v-if="!item.availableInSprint"
              class="dashboard-status-badge dashboard-status-badge--neutral"
            >
              Coming soon
            </span>
          </span>
        </template>
        {{ item.description }}
      </AppPopover>
      <!-- Visually hidden so assistive tech still gets the description without
           relying on hover to reveal the AppPopover tooltip above. -->
      <span class="sr-only">{{ item.description }}</span>
    </span>
  </component>
</template>
